import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { CtySpinner } from '../spinner/CtySpinner';
import { useDitaHtmlProcessor } from '../hooks/useDitaHtmlProcessor';
import { Icon } from '@iconify/react';
import { prepareDitaHtml } from '../utils/ditaHtmlSanitizer';
import { renderDitaHtmlIntoContainer } from '../utils/markdownRenderUtils';
import { DITA_DEFAULT_CONTENT_STYLES } from '../utils/ditaHtmlProcessor';

interface DitaHtmlProcessorProps {
  presignedUrl: string; // Presigned URL pointing to the HTML file
  getPdfLink: (payload: any) => Promise<any>;
  advisorId?: string; // Required for API calls
  lazyLoad?: boolean;
  lazyLoadRootMargin?: string;
  concurrencyLimit?: number;
  onError?: (error: Error, sourceId?: string) => void;
  className?: string;
  enableSanitization?: boolean; // Enable/disable HTML sanitization (default: true)
  maxHtmlSizeMB?: number; // Maximum HTML size in MB (default: 10)
  maxDomNodes?: number; // Maximum DOM nodes (default: 10000)
}

/**
 * DitaHtmlProcessor Component
 * 
 * Common utility component for processing DITA HTML files.
 * Fetches HTML from a presigned URL and processes data-dita-source_id references:
 * 1. Fetches the HTML content from presignedUrl
 * 2. Extracts all data-dita-source_id attributes from <img> and <a> tags
 * 3. Calls the knowledge API to get presigned URLs for each source_id
 * 4. Replaces src (for images) and href (for anchors) with the presigned URLs
 * 5. Supports lazy loading for better performance
 * 
 * Used by:
 * - Converse section (single DITA HTML files)
 * - DITA ZIP file viewer (individual topic files)
 */
export function DitaHtmlProcessor({
  presignedUrl,
  getPdfLink,
  advisorId,
  lazyLoad = true,
  lazyLoadRootMargin = '200px',
  concurrencyLimit = 5,
  onError,
  className = '',
  enableSanitization = true,
  maxHtmlSizeMB = 10,
  maxDomNodes = 10000,
}: DitaHtmlProcessorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [shouldStartProcessing, setShouldStartProcessing] = useState(false);
  const htmlRenderedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const processHtmlContent = useCallback((rawHtml: string): boolean => {
    try {
      setHtmlContent(prepareDitaHtml(rawHtml, { enableSanitization, maxHtmlSizeMB, maxDomNodes }));
      return true;
    } catch (error) {
      setFetchError((error as Error).message || 'Failed to process HTML content');
      console.error('❌ Failed to process HTML:', error);
      onErrorRef.current?.(error as Error);
      return false;
    }
  }, [maxHtmlSizeMB, maxDomNodes, enableSanitization]);

  // Fetch HTML content from presigned URL
  useEffect(() => {
    const fetchHtml = async () => {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Reset flags for new URL
      htmlRenderedRef.current = false;
      setShouldStartProcessing(false);

      // Create new abort controller
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        setIsLoading(true);
        setFetchError(null);

        console.log('🔄 DitaHtmlProcessor: Fetching HTML from presigned URL:', presignedUrl.substring(0, 100) + '...');
        
        const response = await fetch(presignedUrl, { signal: abortController.signal });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch HTML: ${response.status} ${response.statusText}`);
        }

        const rawHtml = await response.text();
        console.log('✅ DitaHtmlProcessor: HTML fetched successfully, length:', rawHtml.length);

        // Process and validate HTML
        const isValid = processHtmlContent(rawHtml);
        if (!isValid) {
          return; // Error already set by processHtmlContent
        }

      } catch (error: any) {
        if (error.name === 'AbortError') {
          console.log('⚠️ DitaHtmlProcessor: Fetch aborted');
          return;
        }
        const errorMessage = error?.message || 'Failed to fetch HTML content';
        setFetchError(errorMessage);
        console.error('❌ DitaHtmlProcessor: Failed to fetch HTML:', error);
        onErrorRef.current?.(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (presignedUrl) {
      fetchHtml();
    }

    // Cleanup on unmount or URL change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [presignedUrl, processHtmlContent]);

  // Set innerHTML directly to prevent React from overwriting DOM manipulations
  useLayoutEffect(() => {
    if (isLoading || !containerRef.current || !htmlContent || htmlRenderedRef.current) {
      return;
    }

    try {
      console.log('📝 DitaHtmlProcessor: Setting innerHTML for container');
      renderDitaHtmlIntoContainer(containerRef.current, htmlContent);
      htmlRenderedRef.current = true;

      setTimeout(() => {
        console.log('🚀 DitaHtmlProcessor: Triggering DITA processing');
        setShouldStartProcessing(true);
      }, 200);
    } catch (error) {
      console.error('❌ DitaHtmlProcessor: Failed to render HTML:', error);
      setFetchError('Failed to render HTML content');
      onErrorRef.current?.(error as Error);
    }
  }, [htmlContent, isLoading]);

  // Use the DITA HTML processor hook
  useDitaHtmlProcessor(containerRef, {
    getPdfLink,
    advisorId,
    lazyLoad,
    lazyLoadRootMargin,
    concurrencyLimit,
    onError,
    trigger: shouldStartProcessing,
  });

  // Loading state
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center h-full w-full ${className}`}>
        <div className="flex flex-col items-center gap-3">
          <CtySpinner size="lg" />
          <p className="text-sm text-default-500">Loading DITA HTML content...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className={`flex flex-col items-center justify-center h-full w-full text-danger ${className}`}>
        <Icon icon="mdi:alert-circle" className="w-12 h-12 mb-2" />
        <p className="text-lg font-medium">Failed to Load HTML</p>
        <p className="text-sm mt-2 max-w-md text-center">{fetchError}</p>
      </div>
    );
  }

  // Note: We don't show "No content" state because we should be loading until we have content
  console.log('✅ DitaHtmlProcessor: Rendering main content, htmlContent length:', htmlContent?.length || 0);
  return (
    <div className={`h-full w-full ${className}`}>
      <div
        ref={containerRef}
        className="h-full w-full dita-content"
      />
      <style>{DITA_DEFAULT_CONTENT_STYLES}</style>
    </div>
  );
}

